// PaginaProduto.js — Página de visualização de um livro, reconstruída em Tailwind
// conforme o mockup "Visualização de Livro" do Figma:
//   Hero (palco da capa + formatos | categorias, título, autores, prova social,
//         ficha rápida, cartão de preço)
//   → Ficha técnica → Sobre o livro (sinopse)
//   → Autores (cards) → Compre junto → Como funciona a compra → Opiniões
//   → Do mesmo autor / Da mesma área → InsigneCoins.
//
// ================= modo="clube": A VITRINE DO LIVRO PARA QUEM ASSINA =================
//
// /clube/livros/:id monta ESTA MESMA página com `modo="clube"` (pedido do dono em
// 10/09/2026: "idêntica ao que já existe, só que sem a parte de valores, compra
// e de venda de outros livros"). Um componente só, e não uma cópia: tudo o que
// melhorar na página do livro — informações, sinopse, autores, opiniões — chega
// às duas sem ninguém lembrar de copiar.
//
// O que o modo tira: cartão de preço (vira o CartaoDoClube, com "Ler agora" e
// "Salvar nos meus e-books"), "Leia um trecho" (quem pode ler o livro inteiro
// não precisa de amostra), o número de compradores, Compre Junto, "Como
// funciona a compra", Do mesmo autor / Da mesma área, o bloco das InsigneCoins
// e a barra de compra do celular.
//
// E ele só vale para quem ASSINA. Visitante, ou assinatura vencida, cai na
// página do livro da loja — a vitrine sem preço para quem não pode ler seria
// uma página sem nenhuma ação possível. O mesmo para livro sem arquivo, que o
// Clube não lê.
import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import useLivroAcademico from "../../../hooks/useLivroAcademico.js";
import useLivrosAdquiridos from "../../../hooks/useLivrosAdquiridos";
import useClube from "../../../hooks/useClube";
import TextoTruncado from "../../utils/TruncarTexto";
import ModalPreview from "./ModalPreview";
import PrecoCard from "./PrecoCard";
import PalcoDaCapa from "./PalcoDaCapa";
import { FichaRapida, FichaTecnica } from "./FichaDoLivro";
import CabecalhoDeSecao from "./CabecalhoDeSecao";
import BotaoVerMais from "./BotaoVerMais";
import FreteDoLivro from "./FreteDoLivro";
import useFreteDoLivro from "./useFreteDoLivro";
import useTelaGrande from "./useTelaGrande";
import AutoresProduto from "./AutoresProduto";
import CompreJunto from "./CompreJunto";
import Opinioes from "./Opinioes";
import OQueDizem from "./OQueDizem";
import LivrosRelacionados from "./LivrosRelacionados";
import BarraCompraMobile from "./BarraCompraMobile";
import Stars from "./Stars";
import { notaDoLivro } from "./notaExibida";
import { COIN_VALOR_TEXTO, COINS_POR_AVALIACAO } from "../../../config/coins";
import Reveal from "../../ui/Reveal";
import BorderGlow from "../../ui/BorderGlow";
import CountUp from "../../ui/CountUp";
import CartaoDoClube from "../../clube/CartaoDoClube";

// Passos do PIX exibidos na página do livro. A dúvida "paguei, e agora?" é a
// principal do checkout desde que o cartão saiu do site: o pagamento é PIX e a
// confirmação é manual, então dizer isso ANTES da compra evita o susto depois.
// O passo 1 dizia "e-book OU impresso", e com o presente o "ou" deixou de
// ser verdade: quem leva o impresso leva a leitura junto. A lista e fixa
// (vale para todo livro), entao o texto precisa ser verdadeiro tambem nos
// titulos SEM presente — por isso "quando o titulo tem os dois formatos",
// e nao uma promessa seca.
const PASSOS_COMPRA = [
  { icone: "bi-book-half", titulo: "Escolha o formato", descricao: "E-book para ler agora, ou impresso para receber em casa. E, quando o t\u00edtulo tem os dois, o e-book vem junto de presente." },
  { icone: "bi-qr-code", titulo: "Pague com PIX", descricao: "O QR Code aparece na hora, ao final do pedido." },
  { icone: "bi-unlock", titulo: "Receba o acesso", descricao: "Confirmado o pagamento, o e-book vai para a aba Meus Livros do seu perfil." },
];

const main = process.env.REACT_APP_MAIN;
const API_URL = process.env.REACT_APP_API;

const isValidPrice = (p) => p && !isNaN(parseFloat(p)) && parseFloat(p) > 0;

// A partir de quantos compradores o número aparece na página.
//
// Livro exibindo "2 compras" comunica que ninguém quis — o efeito é o oposto do
// pretendido, e numa editora acadêmica boa parte do acervo está nessa faixa.
// Abaixo do limiar a informação simplesmente não aparece (o backend continua
// mandando o número; quem decide mostrar é a tela). O espelho no servidor é
// `compras_minimo_exibir` em config/insigne.php.
const MINIMO_COMPRADORES_EXIBIR = 10;

const rolarAteOpinioes = () => {
  const alvo = document.getElementById("opinioes");
  if (alvo) alvo.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Mapeamento de chaves de metadados → rótulo + ícone (ordem de exibição)
const META_DEFS = [
  { keys: ["ISBN"], label: "ISBN", icon: "bi-upc" },
  { keys: ["NumeroPaginas", "numero_paginas"], label: "Nº de Páginas", icon: "bi-file-earmark-text" },
  { keys: ["Edição", "edicao"], label: "Edição", icon: "bi-hash" },
  { keys: ["Editora", "editora"], label: "Editora", icon: "bi-building" },
  { keys: ["Idioma", "idioma"], label: "Idioma", icon: "bi-globe2" },
  { keys: ["Tamanho", "tamanho", "Dimensoes"], label: "Tamanho", icon: "bi-arrows-angle-expand" },
  { keys: ["Formato", "formato"], label: "Formato", icon: "bi-aspect-ratio" },
  { keys: ["Acabamento", "acabamento"], label: "Acabamento", icon: "bi-book" },
  { keys: ["Coloração", "coloracao"], label: "Coloração", icon: "bi-palette" },
  { keys: ["TipoDePapel", "tipo_papel"], label: "Tipo de Papel", icon: "bi-layers" },
];

const PaginaProduto = ({ modo = "loja" }) => {
  const noClube = modo === "clube";
  const { id } = useParams();
  const { ebook } = useLivroAcademico(id);
  // A mesma lista que libera o download em "Meus E-books". Deslogado ela volta
  // vazia e nada muda na tela.
  const { possui: possuiLivro } = useLivrosAdquiridos();
  // Assinante do Clube lê sem comprar: o cartão de preço precisa saber disso
  // para oferecer "ler agora" em vez de mandar a pessoa comprar o que ela já
  // pode ler.
  const { assinante, carregando: carregandoClube } = useClube();

  const [isModalPreviewOpen, setIsModalPreviewOpen] = useState(false);
  const [quantidade, setQuantidade] = useState(1);
  const [opcaoSelecionada, setOpcaoSelecionada] = useState(null);
  const [imgAtiva, setImgAtiva] = useState(0);
  // A sinopse longa começa dobrada nos DOIS modos: no Clube ela mora ao lado do
  // cartão, na loja na seção "Sobre o livro". Os dois nunca aparecem juntos
  // (a da loja é `!noClube`), então dividem o mesmo estado sem conflito.
  const [sinopseAberta, setSinopseAberta] = useState(false);
  // Os passos de "Como funciona a compra", que no celular começam recolhidos.
  const [passosAbertos, setPassosAbertos] = useState(false);
  // O frete mora aqui porque ele é desenhado em DOIS lugares (embaixo da capa
  // no computador, dentro do cartão no celular) e precisa ser um estado só.
  const frete = useFreteDoLivro();
  // ONDE o frete é desenhado. A pergunta é feita em JavaScript, e não com
  // `hidden lg:block`, porque esconder por CSS deixaria DOIS campos de CEP e
  // dois grupos de rádio no documento — ver o cabeçalho de useTelaGrande.
  const telaGrande = useTelaGrande();

  // QUEM DECIDE O PRESENTE E O SERVIDOR, e a loja so obedece.
  //
  // `ebook_de_brinde` ja chega calculado com a condicao inteira (a chave
  // `brinde_ebook_no_fisico`, o titulo ter PDF e ter preco de impresso) —
  // a MESMA que `AcessoLeituraService::ganhouPeloFisico` aplica na hora de
  // liberar a leitura. Deduzir isso aqui criaria uma segunda regra, e a
  // primeira vez que as duas divergissem o site prometeria um arquivo que
  // nao existe.
  //
  // O fallback so cobre resposta de uma API anterior a esta mudanca: sem a
  // flag, nada e prometido. Falhar calado e melhor do que prometer errado.
  const temEbookBrinde = ebook?.ebook_de_brinde === true;

  // ===== CÓPIA DIGITAL GRÁTIS =====
  //
  // `copia_gratis_ativa` já chega decidido pelo servidor: é a marcação do
  // painel MAIS o PDF existir. A tela não pergunta as duas coisas separadas de
  // propósito — quem libera a leitura de verdade é AcessoLeituraService, e uma
  // segunda regra aqui seria a primeira a divergir dele.
  const copiaGratis = ebook?.copia_gratis_ativa === true;


  // Impresso vendido em loja parceira — a mesma condição que o PrecoCard usa
  // para trocar "Comprar" por um link externo.
  const fisicoEmLojaExterna = !!(ebook?.redirecionar_fisico && (ebook?.link_fisico || "").trim());

  const precoFisico = isValidPrice(ebook?.precoImpresso || ebook?.preco_impresso) ? parseFloat(ebook.precoImpresso || ebook.preco_impresso) : null;
  const precoDigital = isValidPrice(ebook?.preco) ? parseFloat(ebook.preco) : null;
  const hasPrecoFisico = precoFisico !== null;
  // ===== O DIGITAL EXISTE MESMO SEM PREÇO =====
  //
  // Esta linha era `precoDigital !== null`, e com ela um livro liberado
  // simplesmente NÃO TINHA versão digital na tela: o título dos anais do
  // Congresso é cadastrado com preço zero, `precoDigital` saía null, a opção
  // "Ebook" não era desenhada e o cartão caía no modo de formato único, com o
  // impresso. O livro que a editora acabou de dar de graça era o único que a
  // pessoa não conseguia abrir.
  //
  // Ter preço e EXISTIR são perguntas diferentes, e só agora elas são duas.
  const hasPrecoDigital = precoDigital !== null || copiaGratis;

  // O PREÇO COM DESCONTO VEM PRONTO DO SERVIDOR, e a tela não calcula nada.
  //
  // Antes ele era montado aqui, a partir do preço promocional que o admin
  // digitava no CMS. O servidor nunca soube desse valor, e por isso o checkout
  // RECUSAVA o pedido: `_validateAndCalculateBookPrices` comparava com
  // `ebooks.preco` e achava diferença. O livro anunciado com desconto era
  // exatamente o livro que ninguém conseguia comprar.
  //
  // Agora quem decide é App\Services\PrecoService, e `preco_com_desconto` chega
  // junto com o livro. Se algum dia esta linha voltar a multiplicar por 0,9
  // aqui dentro, o bug volta inteiro.
  const precoComDesconto = useMemo(() => {
    const p = parseFloat(ebook?.preco_com_desconto);
    return !isNaN(p) && p > 0 && precoDigital != null && p < precoDigital ? p : null;
  }, [ebook, precoDigital]);

  useEffect(() => {
    if (ebook) setOpcaoSelecionada(hasPrecoFisico ? "fisico" : hasPrecoDigital ? "ebook" : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ebook]);

  // Chegou por um link com #opinioes (o convite "Avaliar · +10 coins" de
  // "Meus E-books", ou o e-mail pós-compra): rola até a seção assim que o livro
  // carrega. O React Router não faz isso sozinho, e cair no topo da página
  // depois de clicar em "Avaliar" faz a pessoa achar que o link não funcionou.
  useEffect(() => {
    if (!ebook || window.location.hash !== "#opinioes") return;
    const t = setTimeout(() => {
      document.getElementById("opinioes")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [ebook]);

  // Galeria: usa ebook.imagens[] se existir, senão a imagem única.
  const imagens = (Array.isArray(ebook?.imagens) && ebook.imagens.length ? ebook.imagens : [ebook?.image]).filter(Boolean);

  // Metadados (detalhes_livro JSON + campos diretos)
  const bookData = (() => {
    if (!ebook) return {};
    const data = {};
    if (ebook.detalhes_livro) { try { Object.assign(data, JSON.parse(ebook.detalhes_livro)); } catch { /* ignora */ } }
    const diretos = { ISBN: ebook.isbn, numero_paginas: ebook.numero_paginas, edicao: ebook.edicao, formato: ebook.formato, acabamento: ebook.acabamento, coloracao: ebook.coloracao, tipo_papel: ebook.tipo_papel, idioma: ebook.idioma };
    Object.entries(diretos).forEach(([k, v]) => { if (v != null && v !== "") data[k] = v; });
    return data;
  })();

  const metaItens = META_DEFS
    .map((d) => { const key = d.keys.find((k) => bookData[k] != null && bookData[k] !== ""); return key ? { label: d.label, icon: d.icon, value: bookData[key] } : null; })
    .filter(Boolean);
  // O "Ver Mais" das especificações saiu (17/09/2026): eram no máximo nove
  // itens, e esconder três atrás de um clique custava a pessoa não achar o
  // ISBN. Ver FichaDoLivro.js.

  // ===== OS FORMATOS, DITOS ANTES DE QUALQUER PREÇO =====
  //
  // Mesmas condições que o PrecoCard usa para desenhar as opções — `copiaGratis`,
  // `temEbookBrinde` e `fisicoEmLojaExterna` vêm todos do servidor. A lista só
  // repete, em plaquinhas, o que o cartão já vai oferecer; se as duas
  // divergissem, a plaquinha prometeria uma opção que o cartão não tem.
  const formatos = [
    hasPrecoDigital &&
      (copiaGratis
        ? { chave: "gratis", icone: "bi-unlock-fill", texto: "E-book grátis", tom: "gratis" }
        : { chave: "ebook", icone: "bi-tablet", texto: "E-book" }),
    hasPrecoFisico && {
      chave: "fisico",
      icone: "bi-book",
      texto: fisicoEmLojaExterna ? "Impresso em loja parceira" : "Impresso",
    },
    // O PRESENTE NÃO ENTRA AQUI (17/09/2026). Ele estava, e o print mostrou a
    // mesma promessa duas vezes a 300px de distância: plaquinha debaixo da capa
    // e plaquinha dentro da opção "Livro Físico" do cartão de preço. Fica só no
    // cartão, que é onde ela decide alguma coisa — ao lado do preço do impresso.
  ].filter(Boolean);

  const handleOpcaoChange = (opcao, novaQtd = null) => {
    setOpcaoSelecionada(opcao);
    if (novaQtd !== null) setQuantidade(novaQtd);
    else if (opcao === "ebook") setQuantidade(1);
  };

  const formatDateBR = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleDateString("pt-BR"); };

  // A vitrine do Clube é de quem assina, e de livro que o Clube lê (ver o
  // cabeçalho). `replace`: o "voltar" não pode devolver a pessoa para uma
  // página que a mandaria embora de novo.
  if (noClube && !carregandoClube && !assinante) {
    return <Navigate to={main + "livros/" + id} replace />;
  }
  if (noClube && ebook && ebook.tem_pdf === false) {
    return <Navigate to={main + "livros/" + id} replace />;
  }

  if (!ebook || (noClube && carregandoClube)) {
    // Esqueleto com o formato real da página (capa + coluna de dados): o
    // spinner solto no topo fazia o conteúdo "cair" na tela quando chegava.
    return (
      <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-16">
        <div className="max-w-[1180px] mx-auto animate-pulse" aria-hidden="true">
          <div className="h-3 w-52 rounded-controle bg-insigne-navy-soft mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-8 lg:gap-12">
            <div className="rounded-painel bg-insigne-navy-soft aspect-[4/5] max-w-[420px] w-full" />
            <div className="space-y-4">
              <div className="h-8 w-4/5 rounded-controle bg-insigne-navy-soft" />
              <div className="h-4 w-2/5 rounded-controle bg-insigne-navy-soft" />
              <div className="h-4 w-1/3 rounded-controle bg-insigne-navy-soft" />
              <div className="h-40 rounded-card bg-insigne-navy-soft mt-6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    // O RODAPÉ DA PÁGINA PRECISA CABER ACIMA DA BARRA DE COMPRA.
    //
    // No celular a BarraCompraMobile é `fixed` no rodapé e tem ~64px mais a
    // área segura do aparelho. Com os mesmos `pb-16` de sempre ela cobria o
    // fim do último bloco (as InsigneCoins), e no iPhone a faixa do gesto
    // comia ainda mais. O respiro extra só existe onde a barra existe: no
    // modo clube ela não é montada, e a partir de `lg` ela é escondida.
    <div className={"bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 text-white " + (noClube ? "pb-16" : "pb-28 lg:pb-16")}>
      <div className="max-w-[1180px] mx-auto">
        {/* Breadcrumb */}
        <nav className="text-sm mb-6 flex flex-wrap gap-2 items-center">
          <Link to={main} className="text-insigne-orange hover:underline">Início</Link>
          <span className="text-white/40">/</span>
          {noClube ? (
            // `#acervo`: volta direto para a grade, e não para o topo da
            // página de venda do Clube (ver AcervoDoClube).
            <Link to={main + "clube#acervo"} className="text-insigne-orange hover:underline">Clube Insigne</Link>
          ) : (
            <Link to={main + "livros"} className="text-insigne-orange hover:underline">Livros</Link>
          )}
          <span className="text-white/40">/</span>
          <span className="text-white/60"><TextoTruncado texto={ebook.titulo} limite={40} /></span>
        </nav>

        {/* ===== Hero ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-8 lg:gap-12 mb-12">
          {/* Capa + galeria: ver PalcoDaCapa.js.
              No modo clube não há "Leia um trecho" (quem pode ler o livro
              inteiro não precisa de amostra) nem formatos (são informação de
              venda, e a vitrine do Clube é sem venda). */}
          <div>
            <PalcoDaCapa
              titulo={ebook.titulo}
              imagens={imagens}
              imgAtiva={imgAtiva}
              aoTrocarImagem={setImgAtiva}
              aoLerTrecho={noClube || !ebook.pdf ? null : () => setIsModalPreviewOpen(true)}
              formatos={noClube ? [] : formatos}
            />

            {/* ===== O FRETE, NO VÃO EMBAIXO DA CAPA (18/09/2026) =====
                Pedido do dono, com o print: no computador a coluna da capa
                acabava aqui e o cartão de preço seguia por mais uns 200px, com
                o cálculo de frete dentro dele empurrando o "COMPRAR AGORA"
                para o fim da tela. O frete veio para este vão; o cartão
                encurtou; e a coluna da esquerda deixou de terminar no nada.

                SÓ A PARTIR DE `lg`, e só no impresso vendido aqui: no celular
                esta coluna vem ANTES do título, e um campo de CEP chegaria à
                tela antes do nome e do preço do livro. Lá ele continua dentro
                do cartão (ver PrecoCard). */}
            {!noClube && telaGrande && opcaoSelecionada === "fisico" && !fisicoEmLojaExterna && (
              <FreteDoLivro frete={frete} solto className="mt-4" />
            )}
          </div>

          {/* Detalhes */}
          <div>
            {/* ===== OS SELOS DO LIVRO, NUMA FILEIRA SÓ E ACIMA DO TÍTULO =====
                As categorias ficavam ENTRE "Leia um trecho" e o cartão de
                preço — no meio do caminho entre ler e comprar, empurrando para
                baixo justamente o objeto que converte. E vinham com o `rounded`
                do Bootstrap, que é !important: eram as únicas plaquinhas da
                tela com outro canto.
                Subiram para cima do título porque é ali que um selo trabalha:
                qualifica o que vem depois, em corpo pequeno, sem disputar peso
                com o nome do livro. Vale nas duas versões da página — categoria
                não é preço nem compra. */}
            {ebook.categorias?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2.5">
                {ebook.categorias.map((c, i) => (
                  <span key={i} className="rounded-selo border border-line bg-insigne-navy-mid px-2.5 py-1 text-xs font-semibold text-white/70">{c}</span>
                ))}
              </div>
            )}

            {/* O TÍTULO É O QUE SE LÊ PRIMEIRO, e agora ele tem o corpo disso.
                Era `text-2xl md:text-[32px] font-bold` — o mesmo peso dos
                títulos de seção lá embaixo ("Sobre o Livro", "Autores"), então
                o nome do livro não se destacava da própria página.
                O SUBTÍTULO SAIU DA FRASE DO TÍTULO (27/10/2026). Na mesma fonte
                gigante, só que cinza, ele parecia o título apagado e ainda
                empurrava o nome do livro para três linhas — o dono não gostou.
                Agora vai numa linha própria, na fonte do texto, com o filete
                laranja do site: lê como complemento, e o título fica sozinho
                no corpo grande. O ": " continua para leitor de tela e busca. */}
            <h1 className="mb-0 break-words">
              <span className="titulo-display block text-[2.5rem] md:text-[3.3rem] text-white">
                {ebook.titulo}
              </span>
              {ebook.subtitulo && ebook.subtitulo !== "null" && (
                <span className="mt-3 block border-l-[3px] border-insigne-orange pl-3 font-sans text-base md:text-lg font-medium leading-snug text-white/80 [text-wrap:pretty]">
                  <span className="sr-only">: </span>
                  {ebook.subtitulo}
                </span>
              )}
            </h1>

            {ebook.autores?.length > 0 && (
              // "por Fulana, Beltrano" em branco, e não mais "Autor: Fulana."
              // inteiro em laranja: o laranja ao lado do título competia com o
              // botão de comprar, e o rótulo "Autor:" é o que o "por" diz em
              // três letras. O link de perfil ganha o sublinhado laranja.
              <p className="mt-3 text-[15px] text-white/60">
                por{" "}
                {ebook.autores.map((a, i) => {
                  const nome = typeof a === "object" ? a.nome : a;
                  const linkavel = typeof a === "object" && a.has_profile && a.id;
                  return (
                    <span key={i}>
                      {i > 0 ? ", " : ""}
                      {linkavel ? (
                        <Link
                          to={`${main}autores/${a.id}`}
                          className="font-semibold !text-white underline decoration-insigne-orange decoration-2 underline-offset-4 hover:!text-insigne-orange"
                        >
                          {nome}
                        </Link>
                      ) : (
                        <span className="font-semibold text-white">{nome}</span>
                      )}
                    </span>
                  );
                })}
              </p>
            )}

            {/* ===== Estrelas, avaliações e compradores =====
                As três coisas que respondem "posso confiar neste livro?" numa
                linha só, logo abaixo do autor. Os números são clicáveis e
                rolam até a seção de opiniões — o número solto obrigaria a
                pessoa a procurar onde ele foi calculado. */}
            {(() => {
              const { nota, total, semAvaliacoes, atenuada } = notaDoLivro(ebook);
              const compradores = Number(ebook.compradores_total) || 0;
              return (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm">
                  <button
                    onClick={rolarAteOpinioes}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                    title={semAvaliacoes ? "Este livro ainda não tem avaliações" : "Ver as opiniões"}
                  >
                    <Stars value={nota} size="text-base" className={atenuada ? "opacity-40" : ""} />
                    {!semAvaliacoes && (
                      <span className="font-semibold text-white">{nota.toFixed(1).replace(".", ",")}</span>
                    )}
                  </button>

                  <span className="text-white/25">·</span>

                  <button onClick={rolarAteOpinioes} className="text-white/60 hover:text-white transition-colors">
                    {total === 0 ? "sem avaliações" : `${total} ${total === 1 ? "avaliação" : "avaliações"}`}
                  </button>

                  {/* Abaixo do limiar o dado não aparece — ver MINIMO_COMPRADORES_EXIBIR. */}
                  {!noClube && compradores >= MINIMO_COMPRADORES_EXIBIR && (
                    <>
                      <span className="text-white/25">·</span>
                      <span className="text-white/60">{compradores} compradores</span>
                    </>
                  )}

                  {/* AS VISITAS ENTRARAM NA LINHA DAS ESTRELAS (17/09/2026).
                      Eram uma frase inteira em azul — "Esta página foi vista 32
                      vezes desde 27/03/2026." —, com linha própria entre o autor
                      e o preço, e era a única coisa azul do hero: o olho parava
                      nela antes de chegar ao que importa. Aqui ela é mais um
                      dado de prova social, no mesmo corpo dos vizinhos. A data
                      desceu para o `title`, para quem quiser saber.
                      O `CountUp` continua só com o dado na mão: parte do zero, e
                      animar antes da API responder escreveria "0 visualizações". */}
                  {Number(ebook.views) > 0 && (
                    <>
                      <span className="text-white/25">·</span>
                      <span
                        className="text-white/60"
                        title={ebook.created_at ? `desde ${formatDateBR(ebook.created_at)}` : undefined}
                      >
                        <CountUp to={Number(ebook.views)} />{" "}
                        {Number(ebook.views) === 1 ? "visualização" : "visualizações"}
                      </span>
                    </>
                  )}
                </div>
              );
            })()}

            {/* "Leia um trecho" desceu para debaixo da capa (ver PalcoDaCapa):
                ler um trecho é uma pergunta sobre o livro, e aqui ele ficava no
                meio do caminho entre as estrelas e o preço.
                No lugar dele, as especificações de relance — quantas páginas,
                qual edição — ao lado do preço, que é onde elas decidem. */}
            <FichaRapida itens={metaItens} />

            {noClube ? (
              // ===== O CARTÃO E A SINOPSE, LADO A LADO =====
              //
              // O cartão FLUTUA À ESQUERDA e a sinopse corre ao redor dele: ao
              // abrir "Ler mais", o texto que não cabe ao lado continua ABAIXO
              // do cartão, ocupando o vazio que sobrava ali — em vez de empurrar
              // a página inteira para baixo. Foi o pedido do dono em
              // 11/09/2026, e é o que `float` faz e o `flex` não: no flex as
              // duas colunas são paredes, e o texto nunca passa por baixo.
              //
              // `clear-both` no fim fecha o fluxo: sem ele, a seção seguinte
              // subiria para o lado do cartão quando a sinopse fosse curta.
              //
              // No celular não há float (o cartão ocupa a largura toda) — 248px
              // de cartão mais texto ao lado não cabem em 360.
              //
              // `mt-8` repõe o respiro que a fileira de categorias dava aqui
              // antes de subir para cima do título.
              <div className="mt-8">
                <CartaoDoClube ebookId={id} className="mb-4 sm:float-left sm:mr-5 sm:mb-3" />

                {ebook.sinopse && (() => {
                  const paragrafos = ebook.sinopse.split(/\r?\n/).filter((p) => p.trim());
                  // Dobra só o que é longo de verdade; um "Ler mais" que abre
                  // duas linhas a mais é um clique sem recompensa.
                  const longa = ebook.sinopse.length > 520;
                  return (
                    <section>
                      <h3 className="text-base md:text-lg font-bold mb-2">Sobre o Livro</h3>
                      <div
                        className={
                          "relative text-sm text-white/75 leading-relaxed space-y-2.5 " +
                          (longa && !sinopseAberta ? "max-h-[11.5rem] overflow-hidden" : "")
                        }
                      >
                        {paragrafos.map((p, i) => <p key={i}>{p}</p>)}
                        {longa && !sinopseAberta && (
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12
                                          bg-gradient-to-t from-insigne-bg to-transparent" />
                        )}
                      </div>
                      {longa && (
                        <button
                          type="button"
                          onClick={() => setSinopseAberta((v) => !v)}
                          aria-expanded={sinopseAberta}
                          className="mt-1.5 text-sm font-semibold text-insigne-orange hover:underline"
                        >
                          {sinopseAberta ? "Ler menos" : "Ler mais"}
                        </button>
                      )}
                    </section>
                  );
                })()}

                <div className="clear-both" />
              </div>
            ) : hasPrecoFisico || hasPrecoDigital ? (
              // O CARTÃO DE PREÇO É O OBJETO MAIS DESTACADO DO HERO — é ele que
              // converte. O destaque vem da forma, não de mais uma cor: dentro
              // do PrecoCard a superfície virou `.cartao` (borda de 2px e a
              // sombra dura de 4px do contrato), e ele é a ÚNICA peça com
              // sombra nesta parte da tela — a capa ao lado ficou com a moldura
              // chapada de sempre justamente para não empatar com ele.
              // O raio aqui acompanha o do cartão: é neste invólucro que a
              // `.realce-alvo` pisca quando a barra do celular traz a pessoa
              // de volta, e um anel de canto diferente do card denunciaria a
              // caixa extra.
              // Durante o realce o cartão APAGA a própria sombra dura. O anel é
              // sombra do invólucro, e o filho pinta por cima do pai: com os
              // 4px do `.cartao` no lugar, a sombra cobria o anel à direita e
              // embaixo, e o pisca virava um "L" no alto e à esquerda. Pelos
              // 1,6s do realce o cartão fica chapado, como era antes de ganhar
              // a sombra, e ela volta sozinha quando a barra tira a classe.
              <div id="cartao-de-preco" className="cantoneiras mt-8 rounded-card transition-shadow [&.realce-alvo>.cartao]:shadow-none">
              <PrecoCard
                ebook={ebook}
                opcaoSelecionada={opcaoSelecionada}
                onOpcaoChange={handleOpcaoChange}
                quantidade={quantidade}
                hasPrecoFisico={hasPrecoFisico}
                hasPrecoDigital={hasPrecoDigital}
                precoFisico={precoFisico}
                precoDigital={precoDigital}
                precoComDesconto={precoComDesconto}
                jaPossuiEbook={possuiLivro(id)}
                assinante={assinante}
                ebookId={id}
                temEbookBrinde={temEbookBrinde}
                copiaGratis={copiaGratis}
                frete={frete}
                // No computador o frete está na coluna da capa; aqui ele só
                // entra quando não há aquele vão para ocupar.
                freteNoCartao={!telaGrande}
              />
              {/* O "Leia um trecho" do CELULAR. No desktop ele mora debaixo da
                  capa (PalcoDaCapa); aqui, a coluna da capa vem antes do título
                  e ele empurrava o nome do livro para fora da primeira tela. Logo
                  depois do cartão ele não compete com o comprar: é a segunda
                  pergunta de quem ainda não decidiu. */}
              {ebook.pdf && (
                <button
                  type="button"
                  onClick={() => setIsModalPreviewOpen(true)}
                  className="foco-laranja mt-4 flex w-full items-center justify-center gap-2 rounded-controle border-2 border-insigne-orange py-2.5 text-sm font-semibold text-insigne-orange transition-colors hover:bg-insigne-orange hover:text-paper lg:hidden"
                >
                  <i className="bi bi-book-half" aria-hidden="true" /> Leia um trecho
                </button>
              )}
              </div>
            ) : (
              // Ocupa o lugar do cartão de preço, então usa a MESMA superfície
              // dele (`.cartao`): a receita escrita à mão que estava aqui
              // desenhava outra caixa para o mesmo papel. O ícone saiu do emoji
              // para a fonte de ícones que o resto da página usa.
              <div className="cartao mt-8 p-8 text-center text-white">
                <p className="text-lg mb-2"><i className="bi bi-book mr-2 text-white/60" />Livro Indisponível para Compra</p>
                <p className="text-sm text-white/60">Este livro não possui preços válidos configurados no momento.</p>
              </div>
            )}
          </div>
        </div>

        {/* ===== Ficha técnica ===== ver FichaDoLivro.js */}
        <FichaTecnica itens={metaItens} />

        {/* ===== Sobre o Livro =====
            No modo "clube" ela já apareceu lá em cima, ao lado do cartão. */}
        {!noClube && ebook.sinopse && (() => {
          const paragrafos = ebook.sinopse.split(/\r?\n/).filter((p) => p.trim());
          // Dobra só o que é longo de verdade: um "Ler mais" que abre duas
          // linhas a mais é um clique sem recompensa.
          //
          // DOIS LIMIARES (17/09/2026), porque "longo" depende da largura: 500
          // caracteres são seis linhas no computador e doze num celular de
          // 390px. No celular a sinopse dobra a partir de 420 caracteres; no
          // computador, a partir de 900. Um texto entre os dois dobra só no
          // celular — e o botão dele também só existe lá.
          const longa = ebook.sinopse.length > 900;
          const longaNoCelular = ebook.sinopse.length > 420;
          const dobraSempre = longa && !sinopseAberta;
          const dobraNoCelular = !longa && longaNoCelular && !sinopseAberta;
          return (
            <section className="mb-12" aria-labelledby="sobre-o-livro">
              <CabecalhoDeSecao chapeu="Sinopse" titulo="Sobre o livro" id="sobre-o-livro" />
              {/* LARGURA DE LEITURA (17/09/2026). O texto corria na largura da
                  página inteira, 1.100px, e cada linha tinha perto de 180
                  caracteres — o olho se perde na volta para a linha seguinte.
                  `max-w-3xl` fica perto dos 75 caracteres que um livro impresso
                  usa, e a coluna que sobra à direita é respiro, não buraco. */}
              <div
                className={
                  "relative max-w-3xl space-y-4 text-[15px] leading-7 text-white/80 " +
                  (dobraSempre
                    ? "max-h-[10.5rem] overflow-hidden sm:max-h-[14rem]"
                    : dobraNoCelular
                    ? "max-sm:max-h-[10.5rem] max-sm:overflow-hidden"
                    : "")
                }
              >
                {paragrafos.map((p, i) => <p key={i}>{p}</p>)}
                {(dobraSempre || dobraNoCelular) && (
                  <div
                    className={
                      "pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-insigne-bg to-transparent " +
                      (dobraNoCelular ? "sm:hidden" : "")
                    }
                  />
                )}
              </div>
              {(longa || longaNoCelular) && (
                <button
                  type="button"
                  onClick={() => setSinopseAberta((v) => !v)}
                  aria-expanded={sinopseAberta}
                  className={
                    "foco-laranja mt-2 inline-flex items-center gap-1.5 rounded-controle text-sm font-semibold text-insigne-orange hover:underline " +
                    (longa ? "" : "sm:hidden")
                  }
                >
                  {sinopseAberta ? "Ler menos" : "Ler a sinopse inteira"}
                  <i className={"bi text-xs " + (sinopseAberta ? "bi-chevron-up" : "bi-chevron-down")} aria-hidden="true" />
                </button>
              )}
            </section>
          );
        })()}

        {/* ===== Autores ===== */}
        <AutoresProduto autores={ebook.autores || []} />

        {/* ===== O que dizem sobre este livro (07/10/2026) =====
            Resenha da editora e trechos da própria obra. Alta na página de
            propósito: é a resposta ao livro sem nenhuma avaliação, e lá embaixo,
            depois de "Como funciona a compra", ninguém a encontraria. Fica
            também na vitrine do Clube — não é venda. Some se não houver nada. */}
        <OQueDizem ebook={ebook} />

        {/* ===== Compre junto + Como funciona a compra (PIX) =====
            Os dois são venda — fora da vitrine do Clube. */}
        {!noClube && (
          <>
            <CompreJunto ebook={ebook} />

            {/* O título saiu de DENTRO da caixa: era a única seção da página com
                o nome enfiado no próprio cartão, e com isso ela parecia um
                aviso, não uma seção. */}
            <Reveal as="section" className="mb-12" aria-labelledby="como-comprar">
              <CabecalhoDeSecao chapeu="Pagamento" titulo="Como funciona a compra" id="como-comprar" />
              {/* RECOLHIDO NO CELULAR (17/09/2026). São os mesmos três passos em
                  todo livro do site, ~300px empilhados numa tela de 390px, e
                  quem já comprou uma vez rola por cima deles em toda página. O
                  título continua à vista (é ele que responde "como eu pago?"), e
                  os passos ficam a um toque. No computador eles cabem numa
                  fileira só e continuam abertos. */}
              {/* (18/10/2026) Três cartões com o número em contorno e o
                  ladrilho do ícone, o desenho dos passos do site do Congresso
                  ("Como participar"). Antes era o Stepper de bolinhas, o mesmo
                  do acompanhamento de pedido. */}
              <ol className={"m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-3 " + (passosAbertos ? "" : "hidden sm:grid")}>
                {PASSOS_COMPRA.map((passo, i) => (
                  <li key={passo.titulo} className="cartao relative overflow-hidden p-[1.25rem]">
                    <span aria-hidden="true" className="numero-contorno pointer-events-none absolute right-[0.75rem] top-[0.4rem] text-[4.5rem]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="ladrilho h-11 w-11 text-lg">
                      <i className={"bi " + passo.icone} aria-hidden="true" />
                    </span>
                    <p className="mb-0 mt-[1rem] font-bold text-white">{passo.titulo}</p>
                    <p className="mb-0 mt-[0.35rem] text-sm leading-relaxed text-white/60">{passo.descricao}</p>
                  </li>
                ))}
              </ol>
              <BotaoVerMais
                aberto={passosAbertos}
                aoAlternar={() => setPassosAbertos((v) => !v)}
                textoFechado="Ver os 3 passos: formato, PIX e acesso"
                textoAberto="Esconder os passos"
                className="sm:hidden"
              />
            </Reveal>
          </>
        )}

        {/* ===== Opiniões =====
            A seção existia pronta e nunca era montada: PaginaProduto não a
            importava, e o backend de avaliações não existia. Os dois lados
            foram resolvidos — esta é a prova social do site. */}
        <Opinioes ebookId={id} />

        {/* ===== Do mesmo autor / Da mesma área =====
            Fecha o "beco sem saída" que era o fim desta página. */}
        {!noClube && <LivrosRelacionados ebook={ebook} />}

        {/* ===== InsigneCoins =====
            Era o único cartão da página sem tratamento nenhum, logo abaixo de um
            irmão ("Como funciona a compra") que já acende a borda. Com o mesmo
            `raio={4}` a página fecha com o acabamento com que abriu. Em aparelho
            sem cursor a borda simplesmente nunca acende (App.css, @media hover:none). */}
        {!noClube && (
        <BorderGlow raio={4} className="bloco p-6 text-white/70 text-sm leading-relaxed">
          <p>InsigneCoins são as moedas digitais da Insigne Acadêmica que podem ser utilizadas, a qualquer tempo, para a obtenção de descontos em compras posteriores nesta plataforma de e-books.</p>
          {/* O valor sai de src/config/coins.js — escrever "R$ 0,10" aqui seria a
              sétima cópia do mesmo número, e é assim que a tela passa a divergir
              do que o checkout debita. */}
          <p className="mt-1">
            1 InsigneCoin equivale a R$ {COIN_VALOR_TEXTO}. Você ganha 1 coin a cada
            R$ 1,00 gasto e mais {COINS_POR_AVALIACAO} a cada livro que avaliar.
          </p>
        </BorderGlow>
        )}
      </div>

      {/* Barra fixa de compra (só no celular, e só depois que o cartão de
          preço sai da tela) */}
      {/* Só há o impresso, e ele é vendido fora: a barra não anuncia valor.
          O preço cadastrado aqui é referência interna; quem cobra é a loja
          parceira, que muda o preço sem avisar este site. */}
      {!noClube && (
      <BarraCompraMobile
        // Com o digital liberado a barra mostra "Grátis" e o botão vira "Ler".
        // O preço do IMPRESSO continua valendo, então ele só é anunciado quando
        // não há a liberação para confundir com ele.
        gratis={copiaGratis && !hasPrecoFisico}
        // Com a liberação, o único número que a barra pode anunciar é o do
        // IMPRESSO — o preço digital deixou de valer e mostrá-lo aqui seria
        // cobrar pelo que está sendo dado ali em cima.
        preco={copiaGratis ? precoFisico : precoComDesconto ?? precoDigital ?? precoFisico}
        precoOriginal={copiaGratis ? null : precoComDesconto ? precoDigital : null}
        semPreco={!hasPrecoDigital && fisicoEmLojaExterna}
        rotulo={
          copiaGratis && hasPrecoFisico
            ? "Impresso"
            : precoDigital || precoComDesconto
            ? "E-book a partir de"
            : "Impresso"
        }
      />
      )}

      {!noClube && isModalPreviewOpen && (
        <ModalPreview
          pdfUrl={`${API_URL}get-pdf-preview?pdf_path=${ebook.pdf}`}
          onClose={() => setIsModalPreviewOpen(false)}
          /* ACABOU DE LER O TRECHO — É AQUI QUE SE DECIDE.
             Só para quem não assina e não tem o livro: para os outros dois a
             frase seria, respectivamente, uma oferta do que já está pago e um
             convite a comprar de novo. Sem os números do servidor o rodapé não
             existe, em vez de aparecer com "R$ --". */
          rodape={
            !assinante && !possuiLivro(id) && Number(ebook?.clube?.preco_centavos) > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-3 bg-insigne-navy-deep px-4 py-3">
                <p className="text-sm text-white/80 leading-snug">
                  <i className="bi bi-stars text-insigne-orange mr-1.5" />
                  Gostou? Este e mais{" "}
                  {Number(ebook?.clube?.acervo_total) > 1
                    ? `${Number(ebook.clube.acervo_total) - 1} livros`
                    : "todo o acervo"}{" "}
                  pelo Clube Insigne.
                </p>
                <Link
                  to={main + "clube"}
                  onClick={() => setIsModalPreviewOpen(false)}
                  /* `btn-solido` é a peça pronta do botão laranja cheio
                     (App.css): traz fundo, borda e a inversão no hover. O que
                     estava aqui era a receita refeita à mão. */
                  className="btn-solido shrink-0 rounded-controle px-4 py-2 text-sm font-bold"
                >
                  Ver o Clube
                </Link>
              </div>
            ) : null
          }
        />
      )}
    </div>
  );
};

export default PaginaProduto;
